import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { NotificationDropdown } from '@/components/layout/NotificationDropdown';
import { ProfileMenu } from '@/components/layout/ProfileMenu';
import { BRAND } from '@/config/brand';
import { env } from '@/config/env';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setTheme, toggleSidebar } from '@/store/appSlice';
import type { ReactNode } from 'react';

interface HeaderProps {
  /** @deprecated Branding lives in the sidebar header only */
  moduleLabel?: string;
  /** @deprecated Use moduleLabel — kept for legacy callers */
  title?: string;
  /** @deprecated Not shown in workspace header */
  subtitle?: string;
  leading?: ReactNode;
  extra?: ReactNode;
  onOpenSearch?: () => void;
  onOpenHelp?: () => void;
}

export function Header({
  leading,
  extra,
  onOpenSearch,
  onOpenHelp,
}: HeaderProps) {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.app.theme);
  const sidebarCollapsed = useAppSelector((s) => s.app.sidebarCollapsed);
  const brandName = env.appName || BRAND.name;
  const brandInitial = brandName.charAt(0).toUpperCase();

  return (
    <header className="workspace-header" role="banner">
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={() => dispatch(toggleSidebar())}
          className="workspace-header-icon-btn"
          aria-label="Toggle sidebar"
        >
          <MenuIcon fontSize="small" />
        </button>
        {sidebarCollapsed && (
          <span
            className="workspace-sidebar-logo h-8 w-8 shrink-0 text-xs lg:hidden"
            title={brandName}
          >
            {brandInitial}
          </span>
        )}
        {leading}
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {extra}
        {onOpenSearch && (
          <button
            type="button"
            className="workspace-header-icon-btn hidden sm:flex"
            onClick={onOpenSearch}
            aria-label="Search (Ctrl+K)"
            title="Search (Ctrl+K)"
          >
            <SearchIcon fontSize="small" />
          </button>
        )}
        {onOpenHelp && (
          <button
            type="button"
            className="workspace-header-icon-btn"
            onClick={onOpenHelp}
            aria-label="Help"
            title="Help"
          >
            <HelpOutlineIcon fontSize="small" />
          </button>
        )}
        <button
          type="button"
          className="workspace-header-icon-btn"
          onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </button>
        <NotificationDropdown />
        <ProfileMenu />
      </div>
    </header>
  );
}
